import React, { useState, useEffect, useRef, useCallback } from "react";
import { getGroupRoi, saveGroupRoi } from "../lib/adminApi";
import { ChevronDown, ChevronRight, TrendingUp, Calculator, Lock, Info } from "lucide-react";

const fmtCur = (n, cur, rate = 1) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n) * (Number(rate) || 1);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency: cur || "USD", maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${cur || ""} ${Math.round(v).toLocaleString()}`;
  }
};
const fmtCur2 = (n, cur, rate = 1) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n) * (Number(rate) || 1);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency: cur || "USD", maximumFractionDigits: 2,
    }).format(v);
  } catch {
    return `${cur || ""} ${v.toLocaleString()}`;
  }
};

const SCENARIOS = [
  { key: "conservative", label: "Conservative" },
  { key: "expected", label: "Expected" },
  { key: "optimistic", label: "Optimistic" },
];

function GroupSummary({ calc, cur, rate }) {
  const s = calc.summary || {};
  const scen = s.scenarios || {};
  const investNote = s.investment_note;
  const metricRow = (label, pickAvail, pickUnavail) => (
    <tr className="border-b border-[#F3F4F6] last:border-0">
      <td className="py-1.5 pr-3 text-[11px] text-[#6B7280]">{label}</td>
      {SCENARIOS.map((sc) => {
        const d = scen[sc.key] || {};
        const highlight = sc.key === "expected";
        return (
          <td
            key={sc.key}
            className={`py-1.5 px-3 text-[11px] text-right tabular-nums ${highlight ? "font-semibold text-[#025979]" : "text-[#111827]"}`}
            data-testid={`group-summary-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${sc.key}`}
          >
            {pickAvail(d)}
          </td>
        );
      })}
    </tr>
  );
  const unavail = (
    <span className="inline-flex items-center gap-1 text-[10px] text-[#B45309] italic">
      <Lock className="w-2.5 h-2.5" /> Unavailable
    </span>
  );
  return (
    <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3" data-testid="group-roi-summary">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="w-3.5 h-3.5 text-[#025979]" />
        <span className="text-[11px] font-semibold text-[#111827] uppercase tracking-wide">Group ROI Summary</span>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#E5E7EB]">
            <th className="py-1 pr-3 text-left text-[10px] font-semibold text-[#9CA3AF] uppercase">Metric</th>
            {SCENARIOS.map((sc) => (
              <th
                key={sc.key}
                className={`py-1 px-3 text-right text-[10px] font-semibold uppercase ${sc.key === "expected" ? "text-[#025979]" : "text-[#9CA3AF]"}`}
              >
                {sc.label}{sc.key === "expected" ? " ★" : ""}
                <div className="normal-case text-[9px] text-[#9CA3AF] font-normal">{(scen[sc.key] || {}).scenario_pct}%</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metricRow("Gross Annual Savings", (d) => fmtCur(d.gross_annual_savings, cur, rate))}
          {metricRow("Year 1 Savings", (d) => fmtCur(d.year1_savings, cur, rate))}
          {metricRow("Year 1 Net Benefit", (d) => d.year1_net_benefit != null ? fmtCur(d.year1_net_benefit, cur, rate) : unavail)}
          {metricRow("Payback Period", (d) => d.payback_months != null ? (d.payback_months < 1 ? "<1 mo" : `${d.payback_months} mo`) : unavail)}
          {metricRow("3-Year Cumulative Benefit", (d) => fmtCur(d.three_year_cumulative_benefit, cur, rate))}
          {metricRow("3-Year Net Benefit", (d) => d.three_year_net_benefit != null ? fmtCur(d.three_year_net_benefit, cur, rate) : unavail)}
          {metricRow("3-Year ROI", (d) => d.three_year_roi_pct != null ? `${Math.round(d.three_year_roi_pct).toLocaleString()}%` : unavail)}
        </tbody>
      </table>
      <div className="mt-2 flex items-center justify-between text-[11px] border-t border-[#E5E7EB] pt-2">
        <span className="text-[#6B7280]">Sites in Scope</span>
        <span className="font-medium text-[#111827]" data-testid="group-roi-sites-in-scope">
          {calc.sites_in_scope} of {calc.total_sites}
        </span>
      </div>
      {s.allocated_investment != null && (
        <div className="mt-1 flex items-center justify-between text-[11px]">
          <span className="text-[#6B7280]">Allocated Implementation Investment</span>
          <span className="font-medium text-[#111827]" data-testid="group-roi-allocated-investment">
            {fmtCur2(s.allocated_investment, cur, rate)}
          </span>
        </div>
      )}
      {investNote && (
        <p className="mt-2 flex items-start gap-1.5 text-[10px] text-[#B45309]" data-testid="group-roi-invest-note">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{investNote}</span>
        </p>
      )}
    </div>
  );
}

function TopValueDrivers({ sections, total, cur, rate, groupId }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm p-3" data-testid={`group-roi-top-value-drivers-${groupId}`}>
      <div className="flex items-center gap-2 mb-2">
        <Calculator className="w-3.5 h-3.5 text-[#025979]" />
        <span className="text-[11px] font-semibold text-[#111827] uppercase tracking-wide">Top Value Drivers</span>
        <span className="text-[9px] text-[#9CA3AF] normal-case">(read-only)</span>
      </div>
      <table className="w-full">
        <tbody>
          {sections.map((s) => (
            <tr key={s.code} className="border-b border-[#F3F4F6]">
              <td className="py-1.5 pr-3 text-[11px] text-[#111827]">
                <span className="text-[#025979] font-semibold mr-1.5">{s.code}</span>{s.title}
              </td>
              <td className="py-1.5 pl-2 text-right text-[11px] font-medium text-[#111827] tabular-nums" data-testid={`group-roi-tvd-${groupId}-${s.code}`}>
                {fmtCur(s.subtotal, cur, rate)}
              </td>
            </tr>
          ))}
          <tr>
            <td className="pt-2 pr-3 text-[11px] font-semibold text-[#111827] uppercase tracking-wide">Total Annual Savings</td>
            <td className="pt-2 pl-2 text-right text-[13px] font-bold text-[#025979] tabular-nums" data-testid={`group-roi-tvd-${groupId}-total`}>
              {fmtCur(total, cur, rate)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function CalcInputs({ inputs, boxes, onChange }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm p-3" data-testid="group-roi-inputs">
      <div className="flex items-center gap-2 mb-2">
        <Calculator className="w-3.5 h-3.5 text-[#025979]" />
        <span className="text-[11px] font-semibold text-[#111827] uppercase tracking-wide">Calculation Inputs</span>
        <span className="text-[9px] text-[#9CA3AF] normal-case">(editable — does not change client Step 3 answers)</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
        {inputs.map((f) => (
          <div key={f.key} className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-[#6B7280]">{f.label}</span>
            <input
              type="number"
              value={boxes[f.key] ?? ""}
              placeholder={f.step3_value != null ? String(f.step3_value) : "—"}
              onChange={(e) => onChange(f.key, e.target.value)}
              className="w-32 text-[11px] text-right border border-[#86EFAC] bg-[#F0FDF4] rounded-sm px-1.5 py-1 tabular-nums focus:outline-none focus:ring-1 focus:ring-[#025979]"
              data-testid={`group-roi-input-${f.key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function DriverSection({ section, cur, rate, driverBoxes, onDriverChange }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid={`group-roi-section-${section.code}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#F8FAFC] transition-colors cursor-pointer"
        data-testid={`group-roi-section-${section.code}-toggle`}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-[#9CA3AF]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#9CA3AF]" />}
          <span className="text-[11px] font-semibold text-[#025979]">{section.code}</span>
          <span className="text-[11px] font-medium text-[#111827]">{section.title}</span>
        </div>
        <span className="text-[11px] font-semibold text-[#111827] tabular-nums" data-testid={`group-roi-section-${section.code}-subtotal`}>
          {fmtCur(section.subtotal, cur, rate)}
        </span>
      </div>
      {open && (
        <div className="px-3 pb-3 border-t border-[#E5E7EB] overflow-x-auto">
          <table className="w-full mt-2">
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="py-1 pr-3 text-left text-[10px] font-semibold text-[#9CA3AF] uppercase">Benefit Driver</th>
                <th className="py-1 px-2 text-right text-[10px] font-semibold text-[#9CA3AF] uppercase">Value Used</th>
                <th className="py-1 px-2 text-right text-[10px] font-semibold text-[#9CA3AF] uppercase">Saving / Op / Day</th>
                <th className="py-1 px-2 text-right text-[10px] font-semibold text-[#9CA3AF] uppercase">Saving / Op / Year</th>
                <th className="py-1 pl-2 text-right text-[10px] font-semibold text-[#9CA3AF] uppercase">Annual Group Saving</th>
              </tr>
            </thead>
            <tbody>
              {section.drivers.map((d) => (
                <tr key={d.key} className="border-b border-[#F3F4F6] last:border-0" data-testid={`group-roi-driver-${d.key}`}>
                  <td className="py-1.5 pr-3 text-[11px] text-[#111827]">
                    {d.label}
                    {d.unit ? <span className="text-[9px] text-[#9CA3AF] ml-1">({d.unit})</span> : null}
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <input
                      type="number"
                      value={driverBoxes[d.key] ?? ""}
                      onChange={(e) => onDriverChange(d.key, e.target.value)}
                      className="w-24 text-[11px] text-right border border-[#86EFAC] bg-[#F0FDF4] rounded-sm px-1.5 py-0.5 tabular-nums focus:outline-none focus:ring-1 focus:ring-[#025979]"
                      data-testid={`group-roi-driver-${d.key}-value`}
                    />
                  </td>
                  <td className="py-1.5 px-2 text-right text-[11px] text-[#6B7280] tabular-nums" data-testid={`group-roi-driver-${d.key}-per-day`}>
                    {d.saving_per_op_day != null ? fmtCur2(d.saving_per_op_day, cur, rate) : "—"}
                  </td>
                  <td className="py-1.5 px-2 text-right text-[11px] text-[#6B7280] tabular-nums" data-testid={`group-roi-driver-${d.key}-per-year`}>
                    {d.saving_per_op_year != null ? fmtCur(d.saving_per_op_year, cur, rate) : "—"}
                  </td>
                  <td className="py-1.5 pl-2 text-right text-[11px] font-medium text-[#111827] tabular-nums" data-testid={`group-roi-driver-${d.key}-annual`}>
                    {d.is_base ? <span className="text-[9px] text-[#9CA3AF] italic">base</span> : fmtCur(d.annual, cur, rate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function initBoxes(group) {
  const b = {};
  (group.inputs || []).forEach((f) => {
    b[f.key] = f.effective != null ? String(f.effective) : "";
  });
  return b;
}
function initDriverBoxes(group) {
  const b = {};
  (group.sections || []).forEach((s) => (s.drivers || []).forEach((d) => {
    b[d.key] = d.value_used != null ? String(d.value_used) : "";
  }));
  return b;
}

function GroupRoiCard({ assessmentId, group }) {
  const [open, setOpen] = useState(false);
  const [calc, setCalc] = useState(group);
  const [boxes, setBoxes] = useState(() => initBoxes(group));
  const [driverBoxes, setDriverBoxes] = useState(() => initDriverBoxes(group));
  const [status, setStatus] = useState("");
  const touchedInputs = useRef(new Set());
  const touchedDrivers = useRef(new Set());
  const timer = useRef(null);
  const mounted = useRef(true);
  const boxesRef = useRef(boxes);
  const driverBoxesRef = useRef(driverBoxes);
  useEffect(() => { boxesRef.current = boxes; }, [boxes]);
  useEffect(() => { driverBoxesRef.current = driverBoxes; }, [driverBoxes]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // ROI is calculated in USD; display follows the Implementation-selected currency.
  const cur = calc.display_currency || "USD";
  const rate = calc.exchange_rate || 1;

  const doSave = useCallback(async () => {
    const b = boxesRef.current;
    const db = driverBoxesRef.current;
    const payload = {};
    const inp = {};
    touchedInputs.current.forEach((k) => { inp[k] = b[k] === "" ? "" : b[k]; });
    if (Object.keys(inp).length) payload.input_overrides = inp;
    const dr = {};
    touchedDrivers.current.forEach((k) => { dr[k] = db[k] === "" ? "" : db[k]; });
    if (Object.keys(dr).length) payload.driver_overrides = dr;
    if (!Object.keys(payload).length) return;
    setStatus("Saving…");
    try {
      const r = await saveGroupRoi(assessmentId, group.group_id, payload);
      if (!mounted.current) return;
      setCalc(r.group);
      setStatus("Saved");
      setTimeout(() => mounted.current && setStatus(""), 1200);
    } catch {
      if (mounted.current) { setStatus("Save failed"); setTimeout(() => mounted.current && setStatus(""), 2500); }
    }
  }, [assessmentId, group.group_id]);

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(doSave, 600);
  }, [doSave]);

  const onInputChange = (key, val) => {
    setBoxes((b) => ({ ...b, [key]: val }));
    touchedInputs.current.add(key);
    schedule();
  };
  const onDriverChange = (key, val) => {
    setDriverBoxes((b) => ({ ...b, [key]: val }));
    touchedDrivers.current.add(key);
    schedule();
  };

  const scenarioBadge = (calc.scenario || "").toUpperCase();
  const scen = calc.summary?.scenarios || {};
  const exp = scen[calc.summary?.default_scenario] || scen.expected || {};
  const headMetric = (label, value, testid, strong = false) => (
    <div className="flex flex-col items-end min-w-[92px]" data-testid={testid}>
      <span className="text-[9px] text-[#9CA3AF] uppercase tracking-wide whitespace-nowrap">{label}</span>
      <span className={`text-[12px] tabular-nums whitespace-nowrap ${strong ? "font-bold text-[#025979]" : "font-semibold text-[#111827]"}`}>{value}</span>
    </div>
  );
  const paybackTxt = exp.payback_months != null ? (exp.payback_months < 1 ? "<1 mo" : `${exp.payback_months} mo`) : "—";
  const roiTxt = exp.three_year_roi_pct != null ? `${Math.round(exp.three_year_roi_pct).toLocaleString()}%` : "—";

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid={`group-roi-card-${group.group_id}`}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[#F8FAFC] transition-colors cursor-pointer"
        data-testid={`group-roi-card-${group.group_id}-toggle`}
      >
        <div className="flex items-center gap-2.5">
          {open ? <ChevronDown className="w-4 h-4 text-[#9CA3AF]" /> : <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />}
          <span className="text-[13px] font-semibold text-[#111827]" data-testid={`group-roi-card-${group.group_id}-name`}>{calc.group_name}</span>
          <span className="text-[#D1D5DB]">|</span>
          <span className="text-[10px] font-medium text-[#6D28D9] uppercase tracking-wide">{scenarioBadge}</span>
        </div>
        <div className="flex items-center gap-5 sm:gap-7">
          {headMetric("Investment", calc.allocated_investment != null ? fmtCur(calc.allocated_investment, cur, rate) : "—", `group-roi-card-${group.group_id}-head-investment`)}
          {headMetric("Expected Savings", fmtCur(exp.gross_annual_savings, cur, rate), `group-roi-card-${group.group_id}-head-savings`, true)}
          {headMetric("3-Yr ROI", roiTxt, `group-roi-card-${group.group_id}-head-roi`)}
          {headMetric("Payback", paybackTxt, `group-roi-card-${group.group_id}-head-payback`)}
          <span className="hidden" data-testid={`group-roi-card-${group.group_id}-total`}>{fmtCur(calc.total_annual_cost_savings, cur, rate)}</span>
        </div>
      </div>
      {open && (
        <div className="px-3 pb-3 border-t border-[#E5E7EB] space-y-3 pt-3">
          <div className="flex justify-end h-3">
            {status && <span className="text-[10px] text-[#9CA3AF]" data-testid={`group-roi-status-${group.group_id}`}>{status}</span>}
          </div>
          <GroupSummary calc={calc} cur={cur} rate={rate} />
          <TopValueDrivers sections={calc.sections || []} total={calc.total_annual_cost_savings} cur={cur} rate={rate} groupId={group.group_id} />
          <CalcInputs inputs={calc.inputs || []} boxes={boxes} onChange={onInputChange} />
          <div className="space-y-2">
            {(calc.sections || []).map((s) => (
              <DriverSection key={s.code} section={s} cur={cur} rate={rate} driverBoxes={driverBoxes} onDriverChange={onDriverChange} />
            ))}
          </div>
          <div className="flex items-center justify-between bg-[#025979] text-white rounded-sm px-3 py-2">
            <span className="text-[12px] font-semibold uppercase tracking-wide">Total Annual Cost Savings (S1–S5)</span>
            <span className="text-[15px] font-bold tabular-nums" data-testid={`group-roi-card-${group.group_id}-grand-total`}>
              {fmtCur(calc.total_annual_cost_savings, cur, rate)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function OverallRoi({ overall, cur, rate }) {
  const scen = overall.summary?.scenarios || {};
  const unavail = (
    <span className="inline-flex items-center gap-1 text-[10px] text-[#B45309] italic">
      <Lock className="w-2.5 h-2.5" /> Unavailable
    </span>
  );
  const row = (label, pick) => (
    <tr className="border-b border-[#E5E7EB]/60 last:border-0">
      <td className="py-1.5 pr-3 text-[11px] text-[#E5E7EB]">{label}</td>
      {SCENARIOS.map((sc) => {
        const d = scen[sc.key] || {};
        const hl = sc.key === "expected";
        return (
          <td key={sc.key} className={`py-1.5 px-3 text-[11px] text-right tabular-nums ${hl ? "font-bold text-white" : "text-[#CBD5E1]"}`}
            data-testid={`overall-roi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${sc.key}`}>
            {pick(d)}
          </td>
        );
      })}
    </tr>
  );
  return (
    <div className="rounded-sm overflow-hidden border border-[#025979]" data-testid="overall-roi-summary">
      <div className="bg-[#025979] text-white px-3 py-3">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-4 h-4" />
          <span className="text-[12px] font-semibold uppercase tracking-wide">Overall ROI Summary</span>
          <span className="text-[10px] text-[#9Fc3d3] normal-case">
            Program roll-up · {overall.group_count} group{overall.group_count === 1 ? "" : "s"} · {overall.total_sites_in_scope} site{overall.total_sites_in_scope === 1 ? "" : "s"} in scope
          </span>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/20">
              <th className="py-1 pr-3 text-left text-[10px] font-semibold text-[#9Fc3d3] uppercase">Metric</th>
              {SCENARIOS.map((sc) => (
                <th key={sc.key} className={`py-1 px-3 text-right text-[10px] font-semibold uppercase ${sc.key === "expected" ? "text-white" : "text-[#9Fc3d3]"}`}>
                  {sc.label}{sc.key === "expected" ? " ★" : ""}
                  <div className="normal-case text-[9px] text-[#9Fc3d3] font-normal">{(scen[sc.key] || {}).scenario_pct}%</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {row("Gross Annual Savings", (d) => fmtCur(d.gross_annual_savings, cur, rate))}
            {row("Year 1 Savings", (d) => fmtCur(d.year1_savings, cur, rate))}
            {row("Year 1 Net Benefit", (d) => d.year1_net_benefit != null ? fmtCur(d.year1_net_benefit, cur, rate) : unavail)}
            {row("Payback Period", (d) => d.payback_months != null ? (d.payback_months < 1 ? "<1 mo" : `${d.payback_months} mo`) : unavail)}
            {row("3-Year Cumulative Benefit", (d) => fmtCur(d.three_year_cumulative_benefit, cur, rate))}
            {row("3-Year Net Benefit", (d) => d.three_year_net_benefit != null ? fmtCur(d.three_year_net_benefit, cur, rate) : unavail)}
            {row("3-Year ROI", (d) => d.three_year_roi_pct != null ? `${Math.round(d.three_year_roi_pct).toLocaleString()}%` : unavail)}
          </tbody>
        </table>
      </div>
      <div className="bg-white px-3 py-3" data-testid="overall-roi-value-drivers">
        <span className="text-[11px] font-semibold text-[#111827] uppercase tracking-wide">Program Value Drivers</span>
        <table className="w-full mt-2">
          <tbody>
            {(overall.sections || []).map((s) => (
              <tr key={s.code} className="border-b border-[#F3F4F6]">
                <td className="py-1.5 pr-3 text-[11px] text-[#111827]">
                  <span className="text-[#025979] font-semibold mr-1.5">{s.code}</span>{s.title}
                </td>
                <td className="py-1.5 pl-2 text-right text-[11px] font-medium text-[#111827] tabular-nums" data-testid={`overall-roi-vd-${s.code}`}>
                  {fmtCur(s.subtotal, cur, rate)}
                </td>
              </tr>
            ))}
            <tr>
              <td className="pt-2 pr-3 text-[11px] font-semibold text-[#111827] uppercase tracking-wide">Total Annual Savings</td>
              <td className="pt-2 pl-2 text-right text-[13px] font-bold text-[#025979] tabular-nums" data-testid="overall-roi-vd-total">
                {fmtCur(overall.total_annual_cost_savings, cur, rate)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function GroupRoiTab({ assessmentId }) {
  const [groups, setGroups] = useState(null);
  const [overall, setOverall] = useState(null);
  const [fx, setFx] = useState({ cur: "USD", rate: 1 });
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    getGroupRoi(assessmentId)
      .then((r) => {
        if (!alive) return;
        setGroups(r.groups || []);
        setOverall(r.overall || null);
        setFx({ cur: r.display_currency || "USD", rate: r.exchange_rate || 1 });
      })
      .catch(() => { if (alive) setErr("Failed to load Warehouse Group ROI"); });
    return () => { alive = false; };
  }, [assessmentId]);

  if (err) return <div className="text-xs text-red-600 p-4" data-testid="group-roi-error">{err}</div>;
  if (!groups) return <div className="text-xs text-[#9CA3AF] p-4">Loading Warehouse Group ROI…</div>;
  if (groups.length === 0) {
    return (
      <div className="text-xs text-[#9CA3AF] italic p-4" data-testid="group-roi-empty">
        No Greenfield or Brownfield Warehouse Groups in this assessment. (Cloud Migration groups use the dedicated Cloud Migration ROI model.)
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="group-roi-tab">
      <div>
        <h3 className="text-[13px] font-semibold text-[#111827]">Warehouse Group ROI</h3>
        <p className="text-[11px] text-[#6B7280] mt-0.5">
          Bottom-up S1–S5 benefit calculation per Warehouse Group. All inputs and calculations are in USD;
          monetary values are displayed in the currency selected on the Implementation tab. Editing calculation
          inputs and Benefit Driver values never changes client Step 3 answers or Admin ROI values.
        </p>
      </div>
      {overall && (
        <OverallRoi overall={overall} cur={fx.cur} rate={fx.rate} />
      )}
      {groups.map((g) => (
        <GroupRoiCard key={g.group_id} assessmentId={assessmentId} group={g} />
      ))}
    </div>
  );
}
